// ./src/components/features/Connectors/ConfigEditors/GcpStorageConnectorConfigEditor.tsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  TextInput,
  Textarea,
  Text,
  Anchor,
  Alert,
  Code,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { stringifyObject, parseObject } from "@/utils";
import { GcpStorageConnectorConfig } from "@/types";

export interface GcpStorageConnectorConfigEditorProps {
  /** JSON string of connector configuration */
  value: string;

  /** Callback when configuration changes */
  onChange: (value: string) => void;

  /** Whether the editor is disabled */
  disabled?: boolean;
}

// Default empty configuration
const DEFAULT_CONFIG: GcpStorageConnectorConfig = {
  projectId: "",
  credentials: "",
  bucketName: "",
  pathPrefix: "",
  fileNamePattern: "{{requestId}}.json",
};

/**
 * Parse and validate GCP Storage connector configuration
 */
const parseGcpStorageConfig = (
  jsonString: string
): GcpStorageConnectorConfig => {
  const parsed = parseObject(jsonString, DEFAULT_CONFIG);
  return parsed;
};

export const GcpStorageConnectorConfigEditor: React.FC<
  GcpStorageConnectorConfigEditorProps
> = ({ value, onChange, disabled = false }) => {
  // Parse incoming value to config state
  const [config, setConfig] = useState<GcpStorageConnectorConfig>(() =>
    parseGcpStorageConfig(value)
  );

  // Sync config when external value changes
  useEffect(() => {
    const parsed = parseGcpStorageConfig(value);
    setConfig(parsed);
  }, [value]);

  // Update parent when config changes
  const updateConfig = useCallback(
    (newConfig: GcpStorageConnectorConfig) => {
      setConfig(newConfig);
      onChange(stringifyObject(newConfig, DEFAULT_CONFIG));
    },
    [onChange]
  );

  // Handle field changes
  const handleProjectIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, projectId: e.target.value });
  };

  const handleCredentialsChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    updateConfig({ ...config, credentials: e.target.value });
  };

  const handleBucketNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, bucketName: e.target.value });
  };

  const handlePathPrefixChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, pathPrefix: e.target.value });
  };

  const handleFileNamePatternChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, fileNamePattern: e.target.value });
  };

  // Validate credentials JSON
  const credentialsError = (() => {
    if (!config.credentials) return undefined;
    try {
      JSON.parse(config.credentials);
      return undefined;
    } catch {
      return "Must be valid JSON";
    }
  })();

  return (
    <Stack gap="md">
      {/* Help Alert */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        <Text size="sm">
          Create a service account and download its JSON key from{" "}
          <Anchor
            href="https://console.cloud.google.com/iam-admin/serviceaccounts"
            target="_blank"
            rel="noopener noreferrer"
          >
            GCP Console
          </Anchor>
          {" → IAM & Admin → Service Accounts"}
        </Text>
      </Alert>

      {/* Project ID Field */}
      <TextInput
        label="Project ID"
        placeholder="my-project-123456"
        description="GCP Project ID"
        value={config.projectId}
        onChange={handleProjectIdChange}
        disabled={disabled}
        required
        size="md"
      />

      {/* Credentials Field */}
      <Textarea
        label="Service Account Credentials (JSON)"
        placeholder='{"type": "service_account", "project_id": "...", ...}'
        description="Paste the entire contents of the service account JSON key file"
        value={config.credentials}
        onChange={handleCredentialsChange}
        disabled={disabled}
        required
        size="md"
        minRows={4}
        maxRows={8}
        autosize
        error={credentialsError}
        styles={{
          input: {
            fontFamily: "monospace",
            fontSize: "12px",
          },
        }}
      />

      {/* Bucket Name Field */}
      <TextInput
        label="Bucket Name"
        placeholder="my-form-submissions"
        description="Name of the Cloud Storage bucket (must already exist)"
        value={config.bucketName}
        onChange={handleBucketNameChange}
        disabled={disabled}
        required
        size="md"
      />

      {/* Path Prefix Field */}
      <TextInput
        label="Path Prefix"
        placeholder="submissions/{{formName}}/"
        description="Optional folder path for organizing objects"
        value={config.pathPrefix || ""}
        onChange={handlePathPrefixChange}
        disabled={disabled}
        size="md"
      />

      {/* File Name Pattern Field */}
      <TextInput
        label="File Name Pattern"
        placeholder="{{requestId}}.json"
        description="Pattern for naming files"
        value={config.fileNamePattern || "{{requestId}}.json"}
        onChange={handleFileNamePatternChange}
        disabled={disabled}
        size="md"
      />

      {/* Placeholders Help */}
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        <Text size="sm" fw={500} mb={4}>
          Available placeholders:
        </Text>
        <Text size="xs" component="div">
          <Code>{"{{requestId}}"}</Code> — Submission ID (e.g.,
          20250530143022-ABCD)
          <br />
          <Code>{"{{formId}}"}</Code> — Form UUID
          <br />
          <Code>{"{{formName}}"}</Code> — Form name (sanitized)
          <br />
          <Code>{"{{timestamp}}"}</Code> — Unix timestamp
          <br />
          <Code>{"{{date}}"}</Code> — Date (YYYY-MM-DD)
        </Text>
      </Alert>
    </Stack>
  );
};
